import { parseLocalDateString, toLocalDateTimeString } from '../helpers/date-utils';
import { Widget } from './form-widget.model';

export type SoapNoteMetadata = {
  id?: number;
  date?: string;
  employee_id?: string;
  priority?: number;
  note?: string;
};

type SoapPlanItemMetadata = {
  plan_item_id?: string;
  quantity?: number | null;
};

type SoapNotesPayload = {
  subjective?: unknown[];
  objective?: unknown[];
  assessment?: unknown[];
  plan?: unknown[];
  subjective_attributes?: unknown;
  objective_attributes?: unknown;
  assessment_attributes?: unknown;
  plan_attributes?: unknown;
  subjective_notes_attributes?: unknown;
  objective_notes_attributes?: unknown;
  assessment_notes_attributes?: unknown;
  plan_notes_attributes?: unknown;
  plan_items?: unknown;
};

export class FbEmrSoapNotesWidget extends Widget {
  constructor(data?: unknown) {
    super(data);

    this.name = 'fb-emr-soap-notes';
    this.title = 'SOAP Notes';

    this.properties.text ??= { title: 'Header', value: 'SOAP Notes' };
  }

  public getMetadata(): Record<string, unknown> {
    const items = this.properties?.items?.value as SoapNotesPayload | undefined;

    if (!items || typeof items !== 'object') {
      return {};
    }

    const subjectiveAttributes = this.getAttributes(items, 'subjective');
    const objectiveAttributes = this.getAttributes(items, 'objective');
    const assessmentAttributes = this.getAttributes(items, 'assessment');
    const planAttributes = this.getAttributes(items, 'plan');
    const subjective = this.withAttributeFallback(
      items.subjective,
      subjectiveAttributes.length > 0,
    );
    const objective = this.withAttributeFallback(items.objective, objectiveAttributes.length > 0);
    const assessment = this.withAttributeFallback(
      items.assessment,
      assessmentAttributes.length > 0,
    );
    const planItems = this.getPlanItemsMetadata(items);
    const plan = this.withAttributeFallback(
      items.plan,
      planAttributes.length > 0 || planItems.length > 0,
    );

    const soapNotes: Record<string, unknown> = {};
    if (subjective.length > 0) {
      soapNotes['subjective_notes'] = subjective;
    }
    if (objective.length > 0) {
      soapNotes['objective_notes'] = objective;
    }
    if (assessment.length > 0) {
      soapNotes['assessment_notes'] = assessment;
    }
    if (plan.length > 0) {
      soapNotes['plan_notes'] = plan;
    }
    if (subjectiveAttributes.length > 0) {
      soapNotes['subjective_notes_attributes'] = subjectiveAttributes;
    }
    if (objectiveAttributes.length > 0) {
      soapNotes['objective_notes_attributes'] = objectiveAttributes;
    }
    if (assessmentAttributes.length > 0) {
      soapNotes['assessment_notes_attributes'] = assessmentAttributes;
    }
    if (planAttributes.length > 0) {
      soapNotes['plan_notes_attributes'] = planAttributes;
    }
    if (planItems.length > 0) {
      soapNotes['plan_items'] = planItems;
    }

    return Object.keys(soapNotes).length > 0 ? { soap_notes: soapNotes } : {};
  }

  private buildSection(section: unknown): SoapNoteMetadata[] {
    if (!Array.isArray(section)) {
      return [];
    }

    return section
      .map((item) => this.buildNote(item))
      .filter((note): note is SoapNoteMetadata => !!note);
  }

  private withAttributeFallback(
    section: unknown,
    hasSectionAttributes: boolean,
  ): SoapNoteMetadata[] {
    const notes = this.buildSection(section);
    if (notes.length > 0 || !hasSectionAttributes) {
      return notes;
    }

    const fallback = this.buildAttributeOnlyFallbackNote(section);
    return fallback ? [fallback] : [];
  }

  private buildNote(item: any): SoapNoteMetadata | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const note: SoapNoteMetadata = {};

    const idValue = item.id ?? item.Id ?? item.ID;
    if (this.isFiniteNumber(idValue)) {
      note.id = Number(idValue);
    }

    const employeeId = this.asTrimmedString(
      item.employee_id ?? item.employeeId ?? item.employee ?? item.EmployeeId ?? item.Employee,
    );
    if (employeeId) {
      note.employee_id = employeeId;
    }

    const priorityValue = item.priority ?? item.Priority;
    if (this.isFiniteNumber(priorityValue)) {
      note.priority = Number(priorityValue);
    }

    const textValue = this.asTrimmedString(
      item.note ?? item.Note ?? item.text ?? item.Text ?? item.soap_note,
    );
    if (textValue) {
      note.note = textValue;
    }

    const hasMeaningfulContent = this.hasMeaningfulNoteData(note);
    const date = this.buildNoteDate(
      item.date ?? item.Date ?? item.noteDate ?? item.note_date ?? item.NoteDate,
      hasMeaningfulContent,
    );
    if (date) {
      note.date = date;
    }

    return hasMeaningfulContent ? note : null;
  }

  private hasMeaningfulNoteData(note: SoapNoteMetadata): boolean {
    const hasPriority =
      typeof note.priority === 'number' && Number.isFinite(note.priority) && note.priority !== -1;
    const hasText = typeof note.note === 'string' && note.note.trim() !== '';
    return hasPriority || hasText;
  }

  private buildAttributeOnlyFallbackNote(section: unknown): SoapNoteMetadata | null {
    const row = this.getFirstSectionRecord(section);

    const fallback: SoapNoteMetadata = {};
    if (row) {
      const idValue = row['id'] ?? row['Id'] ?? row['ID'];
      if (this.isFiniteNumber(idValue)) {
        fallback.id = Number(idValue);
      }

      const employeeId = this.asTrimmedString(
        row['employee_id'] ??
          row['employeeId'] ??
          row['employee'] ??
          row['EmployeeId'] ??
          row['Employee'],
      );
      if (employeeId) {
        fallback.employee_id = employeeId;
      }

      const date = this.parseDateOrEmpty(
        row['date'] ?? row['Date'] ?? row['noteDate'] ?? row['note_date'] ?? row['NoteDate'],
      );
      fallback.date = date || toLocalDateTimeString(new Date());
      return fallback;
    }

    fallback.date = toLocalDateTimeString(new Date());
    return fallback;
  }

  private getFirstSectionRecord(section: unknown): Record<string, unknown> | null {
    if (!Array.isArray(section)) {
      return null;
    }

    for (const entry of section) {
      if (entry && typeof entry === 'object') {
        return entry as Record<string, unknown>;
      }
    }

    return null;
  }

  private asTrimmedString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private normalizeArray(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.normalizeId(item))
        .filter((item): item is number => item !== null);
    }

    const normalized = this.normalizeId(value);
    if (normalized !== null) {
      return [normalized];
    }

    return [];
  }

  private normalizePlanItems(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }

    return [];
  }

  private getAttributes(items: SoapNotesPayload, key: string): number[] {
    const direct =
      items[`${key}_notes_attributes` as keyof SoapNotesPayload] ??
      items[`${key}_attributes` as keyof SoapNotesPayload];

    const normalized = this.normalizeArray(direct);
    if (normalized.length > 0) {
      return normalized;
    }

    const section = (items as any)[key];
    if (Array.isArray(section)) {
      for (const item of section) {
        const normalizedItem = this.normalizeArray(item?.assigned_attributes);
        if (normalizedItem.length > 0) {
          return normalizedItem;
        }
      }
    }

    return [];
  }

  private getPlanItemsMetadata(items: SoapNotesPayload): SoapPlanItemMetadata[] {
    const direct = (items as any).plan_items ?? (items as any).planItems;
    const normalizedDirect = this.normalizePlanItems(direct);
    if (normalizedDirect.length > 0) {
      const directQuantities = this.normalizePlanItemsQuantity(
        (items as any).plan_items_quantity ?? (items as any).planItemsQuantity,
      );
      return this.buildPlanItemsMetadata(normalizedDirect, directQuantities);
    }

    const planSection = (items as any).plan;
    if (Array.isArray(planSection)) {
      for (const entry of planSection) {
        const normalized = this.normalizePlanItems(entry?.plan_items ?? entry?.planItems);
        if (normalized.length > 0) {
          const entryQuantities = this.normalizePlanItemsQuantity(
            entry?.plan_items_quantity ?? entry?.planItemsQuantity,
          );
          return this.buildPlanItemsMetadata(normalized, entryQuantities);
        }
      }
    }

    return [];
  }

  private buildPlanItemsMetadata(
    items: unknown[],
    quantities: unknown[] | unknown | null,
  ): SoapPlanItemMetadata[] {
    return items
      .map((item, index) => {
        const base = this.extractPlanItemRecord(item);
        if (!base) {
          return null;
        }
        const quantity =
          Array.isArray(quantities) && quantities[index] !== undefined
            ? quantities[index]
            : !Array.isArray(quantities)
              ? quantities
              : null;
        const normalizedQuantity = this.normalizePlanItemQuantity(quantity);
        return {
          ...base,
          ...(normalizedQuantity !== null ? { quantity: normalizedQuantity } : {}),
        };
      })
      .filter((item): item is SoapPlanItemMetadata => !!item);
  }

  private extractPlanItemRecord(item: unknown): SoapPlanItemMetadata | null {
    if (item && typeof item === 'object') {
      const record = item as Record<string, unknown>;
      const id = record['InvMasterId'] ?? record['plan_item_id'] ?? record['Id'];
      if (!id) {
        return null;
      }
      const rawQuantity = record['AddonQty'] ?? record['quantity'];
      const quantity = this.normalizePlanItemQuantity(rawQuantity);
      return {
        plan_item_id: String(id),
        ...(quantity !== null ? { quantity } : {}),
      };
    }

    if (typeof item === 'string' || typeof item === 'number') {
      const text = String(item).trim();
      if (!text) {
        return null;
      }
      return { plan_item_id: text };
    }

    return null;
  }

  private normalizePlanItemsQuantity(value: unknown): unknown[] | unknown | null {
    if (Array.isArray(value)) {
      return value;
    }

    if (value === null || value === undefined || value === '') {
      return null;
    }

    return value;
  }

  private normalizePlanItemQuantity(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }

    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private normalizeId(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private parseDateOrEmpty(dateValue: unknown): string {
    if (dateValue instanceof Date) {
      return toLocalDateTimeString(dateValue);
    }

    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      if (!trimmed) {
        return '';
      }
      if (trimmed.includes('T')) {
        return trimmed;
      }
      const dateOnly = parseLocalDateString(trimmed);
      return dateOnly ? toLocalDateTimeString(dateOnly) : '';
    }

    return '';
  }

  private buildNoteDate(dateValue: unknown, hasMeaningfulContent: boolean): string {
    const dateOnly = this.parseDateOrEmpty(dateValue);
    if (dateOnly) {
      return dateOnly;
    }

    if (hasMeaningfulContent) {
      // Default to user's current local date only when the note has content.
      return toLocalDateTimeString(new Date());
    }

    // Backend expects YYYY-MM-DD only.
    return '';
  }

  private isFiniteNumber(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num);
  }
}
